// @flow

import type {
  ActionHandlers,
  Reducer,
  Selector,
} from '@travel-frontend/state-handling-sdk';
import type {BookSuccess} from 'redux/v1/book/actions';
import type {BookPsdFallbackSuccess} from 'redux/v1/bookPsdFallback/actions';
import type {FetchJourneySuccess} from 'redux/v1/journey/actions';
import type {JourneyState} from 'redux/v1/journey/JourneyState';
import type {
  Fee,
  Flight,
  Hotel,
  Journey,
  LocalPrices,
  OrganizerSpecial,
  PaymentInfo,
  Reservation,
  Room,
  Supplier,
  TourOperatorRequestParams,
  Upselling,
} from 'redux/v1/journey/models';
// import type {PutTouroperatorVoucherSuccess} from 'redux/v1/touroperatorVoucher/actions';
import type {PaymentType} from 'types';

import {
  createReducerUsingActionHandlers,
  ERROR,
  PENDING,
  SUCCESS,
} from '@travel-frontend/state-handling-sdk';
import {createSelector} from 'reselect';

import {PUT_JOURNEY_BOOK} from 'redux/v1/book/actions';
import {PUT_JOURNEY_BOOK_PSDFALLBACK} from 'redux/v1/bookPsdFallback/actions';
import {FETCH_JOURNEY, FETCH_JOURNEY_CONFIRM} from 'redux/v1/journey/actions';
import {
  SELECT_RESERVATION,
  UNSELECT_RESERVATION,
} from 'redux/v1/reservation/select/actions';
// import {PUT_TOUROPERATOR_VOUCHER} from 'redux/v1/touroperatorVoucher/actions';
import {journeyState} from 'redux/v1/V1State';

export const initialState: JourneyState = {
  loading: false,
};

const actionCreators: ActionHandlers<JourneyState> = {
  [PENDING(FETCH_JOURNEY)]: (state: JourneyState) => ({
    ...state,
    loading: true,
  }),
  [SUCCESS(FETCH_JOURNEY)]: (
    state: JourneyState,
    action: FetchJourneySuccess
  ) => ({
    ...state,
    loading: false,
    data: action.data,
  }),
  [SUCCESS(FETCH_JOURNEY_CONFIRM)]: (
    state: JourneyState,
    action: FetchJourneySuccess
  ) => ({
    ...state,
    data: {...state.data, booked: true, bookingId: action.data.bookingId},
  }),
  [ERROR(FETCH_JOURNEY)]: (state: JourneyState, action: any) => ({
    ...state,
    loading: false,
    error: action.error,
  }),
  [SUCCESS(PUT_JOURNEY_BOOK_PSDFALLBACK)]: (
    state: JourneyState,
    action: BookPsdFallbackSuccess
  ) => ({
    ...state,
    data: {...state.data, booked: true, bookingId: action.data.bookingId},
  }),
  [SUCCESS(PUT_JOURNEY_BOOK)]: (state: JourneyState, action: BookSuccess) => ({
    ...state,
    data: {...state.data, booked: true, bookingId: action.data.bookingId},
  }),
  [SUCCESS(SELECT_RESERVATION)]: (state: JourneyState, action: any) => ({
    ...state,
    data: {
      ...state.data,
      reservation: {
        ...state.data?.reservation,
        selected: true,
      },
    },
  }),
  [SUCCESS(UNSELECT_RESERVATION)]: (state: JourneyState, action: any) => ({
    ...state,
    data: {
      ...state.data,
      reservation: {
        ...state.data?.reservation,
        selected: false,
      },
    },
  }),
  // [SUCCESS(PUT_TOUROPERATOR_VOUCHER)]: (
  //   state: JourneyState,
  //   action: PutTouroperatorVoucherSuccess
  // ) => ({
  //   ...state,
  //   data: {...state.data, organizerCreditCode: action.creditCode},
  // }),
};

export const journeyReducer: Reducer<JourneyState> =
  // $FlowIgnore
  createReducerUsingActionHandlers<JourneyState>(initialState, actionCreators);

export const isJourneyLoaded: Selector<boolean> = createSelector(
  journeyState,
  (state: JourneyState) => !state.loading && state.data !== undefined
);

export const getJourney: Selector<any> = createSelector(
  journeyState,
  (state: JourneyState) => state.data ?? state.error
);

export const getBooked: Selector<boolean> = createSelector(
  journeyState,
  (state: JourneyState) => state.data?.booked
);

export const getData: Selector<Journey> = createSelector(
  [journeyState, isJourneyLoaded],
  (state: JourneyState, loaded: boolean) => (loaded ? state.data : {})
);

export const getHotel: Selector<Hotel> = createSelector(
  [getData, isJourneyLoaded],
  (state: Journey, loaded: boolean) => (loaded ? state.hotel : {})
);

export const getTravelerAdultCount: Selector<number> = createSelector(
  journeyState,
  (state: JourneyState) => state.data?.adults
);

export const getTravelerChildCount: Selector<number> = createSelector(
  journeyState,
  (state: JourneyState) => state.data?.children.length
);

export const getChildren: Selector<string[]> = createSelector(
  journeyState,
  (state: JourneyState) => state.data?.children
);

export const getDepartureDate: Selector<string> = createSelector(
  journeyState,
  (state: JourneyState) => state.data?.departureDate || ''
);

export const getReturnDate: Selector<string> = createSelector(
  journeyState,
  (state: JourneyState) => state.data?.returnDate || ''
);

export const getPersonCount: Selector<number> = createSelector(
  journeyState,
  (state: JourneyState) => state.data?.personCount || 0
);

export const getBookingCode: Selector<string> = createSelector(
  journeyState,
  (state: JourneyState) => state.data?.bookingCode || ''
);

export const getBookingId: Selector<string> = createSelector(
  journeyState,
  (state: JourneyState) => state.data?.bookingId || ''
);

export const getCateringType: Selector<string> = createSelector(
  [getData, isJourneyLoaded],
  (state: Journey, loaded: boolean) => (loaded ? state.cateringType : '')
);

export const getOrganizerDiscount: Selector<number> = createSelector(
  journeyState,
  (state: JourneyState) => state.data?.organizerDiscount
);

export const getPersonList: Selector<string[]> = createSelector(
  journeyState,
  (state: JourneyState) => state.data?.personList
);

export const getPricePerTraveler: Selector<number[]> = createSelector(
  journeyState,
  (state: JourneyState) => state.data?.pricePerTraveler
);

export const getProvider: Selector<string> = createSelector(
  [getData, isJourneyLoaded],
  (state: Journey, loaded: boolean) => (loaded ? state.provider : '')
);

export const getPrice: Selector<number> = createSelector(
  [getData, isJourneyLoaded],
  (state: Journey, loaded: boolean) => (loaded ? state.price : 0)
);

export const getHygieneInformationLink: Selector<string> = createSelector(
  [getData, isJourneyLoaded],
  (state: Journey, loaded: boolean) =>
    loaded ? state.hygieneInformationLink : ''
);

export const getHygieneInformationTransitLinks: Selector<string[]> =
  createSelector(
    [getData, isJourneyLoaded],
    (state: Journey, loaded: boolean) =>
      loaded ? state.hygieneInformationTransitLinks : []
  );

export const getOrganizer: Selector<number> = createSelector(
  [getData, isJourneyLoaded],
  (state: Journey, loaded: boolean) => (loaded ? state.organizer : '')
);

export const getOrganizerCreditCode: Selector<string> = createSelector(
  journeyState,
  (state: JourneyState) => (state.data ? state.data.organizerCreditCode : '')
);

export const getPaymentInfo: Selector<PaymentInfo> = createSelector(
  journeyState,
  (state: JourneyState) => state.data?.paymentInfo
);

export const getFees: Selector<Fee[]> = createSelector(
  journeyState,
  (state: JourneyState) => state.data?.fees || []
);

export const getHotelId: Selector<string | null> = createSelector(
  [getData, isJourneyLoaded],
  (state: Journey, loaded: boolean) =>
    loaded && state.hotelIdType === 'giataid' ? state.hotelId : null
);

// hotel
export const getCountry: Selector<string> = createSelector(
  journeyState,
  (state: JourneyState) => state?.data?.hotel?.country
);

export const getCountryCode: Selector<string> = createSelector(
  journeyState,
  (state: JourneyState) => state?.data?.hotel?.countryCode
);

export const getHotelName: Selector<string> = createSelector(
  [getHotel, isJourneyLoaded],
  (hotel: Hotel, loaded: boolean) => (loaded ? hotel.name : '')
);

export const getHotelStars: Selector<number> = createSelector(
  [getHotel, isJourneyLoaded],
  (hotel: Hotel, loaded: boolean) => (loaded ? hotel.stars : 0)
);

export const getHotelCategoryVerified: Selector<number> = createSelector(
  [getHotel, isJourneyLoaded],
  (hotel: Hotel, loaded: boolean) => loaded && hotel.hotelCategoryVerified
);

// supplier
export const getSupplier: Selector<Supplier> = createSelector(
  [getData, isJourneyLoaded],
  (state: Journey, loaded: boolean) => (loaded ? state.supplier : {})
);

export const getCancellationFee: Selector<OrganizerSpecial | void> =
  createSelector(
    journeyState,
    (state: JourneyState) => state.data?.supplier?.cancellationFee
  );

export const getSupplierSpecialInfo: Selector<OrganizerSpecial[]> =
  createSelector(
    [getSupplier, isJourneyLoaded],
    (state: Supplier, loaded: boolean) => (loaded ? state.specialInfo : [])
  );

// flights
export const getFlights: Selector<Flight[]> = createSelector(
  [getData, isJourneyLoaded],
  (state: Journey, loaded: boolean) => (loaded ? state.flights : [])
);

// paymentMethods
export const getPaymentMethods: Selector<PaymentType[]> = createSelector(
  [getData, isJourneyLoaded],
  (state: Journey, loaded: boolean) => (loaded ? state.paymentMethods : [])
);

// reservation
export const getReservation: Selector<Reservation> = createSelector(
  [getData, isJourneyLoaded],
  (state: Journey, loaded: boolean) => (loaded ? state.reservation : null)
);

// tour operator request params
export const getTourOperatorRequestParams: Selector<TourOperatorRequestParams | null> =
  createSelector(
    [getData, getHotelId],
    (
      {organizer, departureDate, returnDate, port, bookingCode}: Journey,
      hotelId: string | null
    ) =>
      hotelId && organizer && departureDate && returnDate
        ? {
            hotelId,
            organizer,
            departureDate,
            returnDate,
            port,
            bookingCode,
          }
        : null
  );

export const getCarRentalLink: Selector<string> = createSelector(
  journeyState,
  (state: JourneyState) => state.data?.carrentalLink
);

export const getTransferInclusive: Selector<string> = createSelector(
  journeyState,
  (state: JourneyState) => state.data?.transferInclusive
);

export const getIsOwnArrival: Selector<boolean> = createSelector(
  journeyState,
  (state: JourneyState) => state.data?.port === 653
);

export const getRooms: Selector<Room> = createSelector(
  journeyState,
  (state: JourneyState) => state.data?.rooms || []
);

export const getLocalPrices: Selector<LocalPrices> = createSelector(
  journeyState,
  (state: JourneyState) => state.data?.localPrices || {}
);

export const getUpselling: Selector<Upselling> = createSelector(
  journeyState,
  (state: JourneyState) => state.data?.upselling || undefined
);
